import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  expenseReportsApi,
  ReportFields,
  ReportForm,
  TReportForm,
} from 'src/services/expense-reports'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { organizationQueries } from 'src/services/user'
import { exportTemplateQueries } from 'src/services/export-templates/queries'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { Input } from '../input'
import { Button } from '../button'
import { Spinner } from '../spinner'
import { CustomSelect } from '../custom-select'
import { ErrorsList } from '../errors'
import { SelectWrapper } from '../select-wrapper'
import { downloadReport } from 'src/utils/download'
import { ArrowDownTrayIcon, SparklesIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GenerateReportModal } from './generate-report-modal'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { Gate } from 'src/components/gate'
import { useAsyncJobStore } from 'src/stores/async-jobs'
import { taskTypeEnum, buildOptimisticJob } from 'src/services/async-jobs/models'

interface ReportDetailPageProps {
  reportId?: string
}

const ReportDetailPage: React.FC<ReportDetailPageProps> = ({ reportId: propReportId }) => {
  const { t } = useTranslation()
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const restoreActive = useAsyncJobStore((s) => s.actions.restoreActive)
  const { createFormFieldChangeHandler, form, overrideForm } = useTnForm<TReportForm>()

  const reportId = propReportId || id

  // Queries
  const { data: organizations, isLoading: loadingOrgs } = useQuery(organizationQueries.list())
  const { data: report, isLoading: loadingReport } = useQuery(
    expenseReportQueries.retrieve(reportId ?? ''),
  )

  const selectedOrgId = form.organization?.value?.value
  const { data: templates, isLoading: loadingTemplates } = useQuery(
    exportTemplateQueries.list({
      filters: selectedOrgId ? { organization: selectedOrgId } : undefined,
    }),
  )

  const templateOptions = useMemo(
    () =>
      templates?.results?.map((t) => ({
        label: t.name,
        value: t.id,
      })) || [],
    [templates],
  )

  // Mutations
  const { mutate: createReportMutation, isPending: isCreating } = useMutation({
    mutationFn: (data: any) => expenseReportsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseReportQueries.all() })
      navigate('/expense-reports')
    },
  })

  const { mutate: updateReportMutation, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      expenseReportsApi.update({ ...data, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseReportQueries.all() })
      navigate('/expense-reports')
    },
  })

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: expenseReportsApi.remove,
    queryKey: expenseReportQueries.all(),
    onSuccess: () => navigate('/expense-reports'),
  })

  const { mutate: generateReportMutation, isPending: isGenerating } = useMutation({
    mutationFn: ({ id, templateId }: { id: string; templateId?: string | null }) =>
      expenseReportsApi.csc.generateReport({ id, templateId }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: expenseReportQueries.retrieve(reportId ?? '').queryKey,
      })
      // Light up the report-export spinner immediately.
      if (data.asyncJobId) {
        restoreActive(
          buildOptimisticJob({
            id: data.asyncJobId,
            taskType: taskTypeEnum.EXPORT_REPORT,
            entityId: variables.id,
            entityType: 'ExpenseReport',
            label: t('asyncJobs.generatingReport'),
          }),
        )
      }
    },
  })

  const [showGenerateModal, setShowGenerateModal] = useState(false)

  // Load report data if editing
  useEffect(() => {
    if (report && !loadingReport && reportId) {
      const organizationRef = report.organizationRef
        ? { label: report.organizationRef.name, value: report.organizationRef.id }
        : undefined

      const templateRef = report.defaultTemplateRef
        ? { label: report.defaultTemplateRef.name, value: report.defaultTemplateRef.id }
        : undefined

      overrideForm(
        ReportForm.create({
          _name: report.name,
          notes: report.notes || '',
          organization: organizationRef,
          defaultTemplate: templateRef,
        }) as TReportForm,
      )
    }
  }, [report, loadingReport, reportId, overrideForm])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid) {
      return
    }

    const formData = form.val

    if (reportId) {
      updateReportMutation({ id: reportId, data: formData })
    } else {
      createReportMutation(formData)
    }
  }

  const handleCancel = () => {
    navigate('/expense-reports')
  }

  const handleDelete = () => {
    if (reportId) {
      requestDelete(reportId, report?.name)
    }
  }

  const handleGenerateReport = () => {
    setShowGenerateModal(true)
  }

  const handleConfirmGenerate = (templateId: string | null) => {
    if (reportId) {
      generateReportMutation({ id: reportId, templateId })
    }
  }

  const handleDownloadReport = () => {
    if (report?.asset) {
      downloadReport(report.asset)
    }
  }

  const organizationOptions =
    organizations?.results?.map((org) => ({
      label: org.name,
      value: org.id,
    })) || []

  const isLoading = loadingReport
  const isSaving = isUpdating || isCreating

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-bold text-primary-600">
        {reportId ? t('reportDetail.editTitle') : t('reportDetail.createTitle')}
      </h2>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Basic Information Section */}
          <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
            <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
              {t('reportDetail.basicInfo')}
            </h3>
            <div className="space-y-4">
              <div>
                <Input
                  label={form._name.label}
                  placeholder={form._name.placeholder}
                  value={form._name.value}
                  onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
                  onBlur={() => form._name.validate()}
                  disabled={isSaving}
                  data-testid="report-name-input"
                />
                <ErrorsList errors={form._name.errors} />
              </div>

              <SelectWrapper
                label={form.organization.label}
                onBlur={() => form.organization.validate()}
                data-testid="organization-select-wrapper"
              >
                <CustomSelect
                  searchable
                  clearable
                  options={organizationOptions}
                  values={form.organization.value ? [form.organization.value] : []}
                  loading={loadingOrgs}
                  disabled={isSaving}
                  onChange={(values) => {
                    if (values.length > 0) {
                      createFormFieldChangeHandler(form.organization)(values[0])
                    } else {
                      createFormFieldChangeHandler(form.organization)(undefined)
                    }
                    // Clear template when org changes
                    createFormFieldChangeHandler(form.defaultTemplate)(undefined)
                  }}
                />
                <ErrorsList errors={form.organization.errors} />
              </SelectWrapper>

              <SelectWrapper
                label={form.defaultTemplate.label}
                data-testid="default-template-select-wrapper"
              >
                <CustomSelect
                  searchable
                  clearable
                  options={templateOptions}
                  values={form.defaultTemplate.value ? [form.defaultTemplate.value] : []}
                  loading={loadingTemplates}
                  disabled={isSaving || !selectedOrgId}
                  placeholder={
                    !selectedOrgId
                      ? t('reportDetail.selectOrgFirst')
                      : form.defaultTemplate.placeholder
                  }
                  onChange={(values) => {
                    if (values.length > 0) {
                      createFormFieldChangeHandler(form.defaultTemplate)(values[0])
                    } else {
                      createFormFieldChangeHandler(form.defaultTemplate)(undefined)
                    }
                  }}
                />
              </SelectWrapper>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {form.notes.label}
                </label>
                <textarea
                  data-testid="report-notes"
                  className="w-full rounded-md border bg-surface p-2 text-text-primary focus:ring-2 focus:ring-border-strong"
                  rows={4}
                  placeholder={form.notes.placeholder}
                  value={form.notes.value || ''}
                  onChange={(e) => createFormFieldChangeHandler(form.notes)(e.target.value)}
                  onBlur={() => form.notes.validate()}
                  disabled={isSaving}
                />
                <ErrorsList errors={form.notes.errors} />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 lg:col-span-1">
          {/* Read-only Information Section */}
          {reportId && report && (
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
                {t('reportDetail.reportStatus')}
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-placeholder">{t('common.status')}</p>
                  <p className="font-semibold" data-testid="report-status">
                    {report.status || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-placeholder">{t('reportDetail.submitted')}</p>
                  <p data-testid="report-is-submitted">
                    {report.isSubmitted ? t('common.yes') : t('common.no')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-placeholder">{t('reportDetail.approved')}</p>
                  <p data-testid="report-is-approved">
                    {report.isApproved ? t('common.yes') : t('common.no')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-placeholder">
                    {t('reportDetail.exportTemplate')}
                  </p>
                  <p data-testid="report-default-template">
                    {report.defaultTemplateRef?.name || t('reportDetail.systemDefault')}
                  </p>
                </div>
                {report.dateReportGenerated && (
                  <div>
                    <p className="text-sm text-text-placeholder">
                      {t('reportDetail.dateGenerated')}
                    </p>
                    <p data-testid="report-date-generated">
                      {new Date(report.dateReportGenerated).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions Section */}
          {reportId && (
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
                {t('reportDetail.actionsTitle')}
              </h3>
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleGenerateReport}
                  disabled={isGenerating}
                  className="w-full"
                >
                  <SparklesIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                  {isGenerating ? t('reportDetail.generatingReport') : t('common.generate')}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleDownloadReport}
                  disabled={!report?.asset || isSaving}
                  className="w-full"
                >
                  <ArrowDownTrayIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                  {t('common.download')}
                </Button>
                <Gate permission="reports.delete">
                  <Button
                    type="button"
                    variant="danger"
                    onClick={handleDelete}
                    disabled={dialogProps.isPending || isSaving}
                    className="w-full"
                  >
                    <TrashIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                    {dialogProps.isPending ? t('reportDetail.deletingReport') : t('common.delete')}
                  </Button>
                </Gate>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Actions */}
      <div className="mt-6 flex flex-col justify-end space-x-4 space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
        <Button
          data-testid="report-cancel-button"
          type="button"
          variant="secondary"
          onClick={handleCancel}
          disabled={isSaving}
        >
          {t('common.cancel')}
        </Button>
        <Button
          data-testid="report-submit-button"
          variant="primary"
          type="submit"
          disabled={isSaving}
        >
          {isSaving ? (reportId ? t('common.saving') : t('common.creating')) : t('common.save')}
        </Button>
      </div>

      <GenerateReportModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleConfirmGenerate}
        organizationId={report?.organization}
        defaultTemplateRef={report?.defaultTemplateRef}
        isGenerating={isGenerating}
      />

      <ConfirmDeleteDialog {...dialogProps} />
    </form>
  )
}

export const ReportDetailPageWrapper: React.FC = () => {
  const { id } = useParams<{ id?: string }>()

  return (
    <FormProvider<ReportFields> formClass={ReportForm} key={id}>
      <ReportDetailPage />
    </FormProvider>
  )
}
